import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, ChevronUp, ChevronDown, Eye, EyeOff, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

/**
 * Move, hide and edit a homepage section from the storefront itself.
 *
 * Reordering previously meant leaving the page for /admin/homepage, dragging
 * rows in a list of section-type labels, and coming back to find out what it
 * looked like. The order of sections is a visual decision, so it should be made
 * while looking at the page — which is the whole argument for Quick Edit.
 *
 * Hiding is offered; deleting is not. A delete is recoverable (Design History
 * keeps the only copy of the row) but it is a poor fit for a hover toolbar on
 * the live storefront, where the cost of a misclick is a section vanishing for
 * every visitor. Hiding does the same job reversibly and in place.
 */
export default function SectionControls({
  sectionId,
  isActive,
  canMoveUp,
  canMoveDown,
  onMove,
  label,
}: {
  sectionId: string;
  isActive: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** Returns the full reordered list, so the server gets one atomic update. */
  onMove: (direction: 'up' | 'down') => { id: string; display_order: number }[];
  label: string;
}) {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);
  const [busy, setBusy] = useState<'move' | 'visibility' | null>(null);

  const reorder = useMutation({
    mutationFn: (sections: { id: string; display_order: number }[]) =>
      api.put('/admin/homepage/reorder', { sections }),
  });

  const setVisibility = useMutation({
    mutationFn: (next: boolean) => api.put(`/admin/homepage/${sectionId}`, { is_active: next }),
  });

  async function run(kind: 'move' | 'visibility', fn: () => Promise<unknown>) {
    setBusy(kind);
    try {
      await fn();
      // The public homepage and the admin builder read the same key.
      await queryClient.invalidateQueries({ queryKey: ['homepage'] });
    } catch (err) {
      // Same rule as the inline text editor: say what happened. A toolbar that
      // silently does nothing reads as a broken button.
      addToast(
        err instanceof Error && err.message ? `Could not save: ${err.message}` : 'Could not save that change',
        'error',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md bg-gray-900/90 p-1 text-white shadow-lg hover-reveal hover-reveal-qe focus-within:opacity-100">
      <IconButton
        label={`Move ${label} up`}
        disabled={!canMoveUp || busy !== null}
        onClick={() => run('move', () => reorder.mutateAsync(onMove('up')))}
      >
        <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
      </IconButton>
      <IconButton
        label={`Move ${label} down`}
        disabled={!canMoveDown || busy !== null}
        onClick={() => run('move', () => reorder.mutateAsync(onMove('down')))}
      >
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      </IconButton>
      <IconButton
        label={isActive ? `Hide ${label}` : `Show ${label}`}
        disabled={busy !== null}
        onClick={() => run('visibility', () => setVisibility.mutateAsync(!isActive))}
      >
        {isActive ? (
          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </IconButton>
      <span className="mx-0.5 h-4 w-px bg-white/25" aria-hidden="true" />
      <Link
        to="/admin/homepage"
        className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        aria-label={`Edit ${label}`}
      >
        {busy ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        ) : (
          <Pencil className="h-3 w-3" aria-hidden="true" />
        )}
        Edit
      </Link>
    </div>
  );
}

function IconButton({
  label, disabled, onClick, children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="rounded p-1 hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
    >
      {children}
    </button>
  );
}
